/**
 * ATL-2052 — the mirror-intent POLICY, read the way the dual-write gate reads its own (ATL-911).
 *
 * Two Remote Config keys decide whether ATL-1459 mirror intents cover an organization:
 *   - `mirror_intent_org_allowlist` — `*` or a JSON array of org Firestore ids;
 *   - `mirror_intent_sb_readers`    — `on` extends intents to every organization that READS Supabase.
 *     Unset (or anything else) is off, which is the behaviour before this key existed.
 *
 * The device's Remote Config SDK can report a successful fetch and read every key EMPTY, for whole
 * sessions (ATL-911). For the dual-write gate that was fixed by asking the backend, which reads the
 * same template with the admin SDK (`GET /config/dual-write-policy`). Without the same fallback here
 * a till on such a device would run with no mirror intent, silently, while its organization is on
 * the allowlist. So each key is read like `dualWrite.ts`'s `policyString`: Remote Config when it
 * reads a value, else the backend's copy when this session has one. The device value always wins —
 * the fallback stands in for a broken channel, it never overrides a working one.
 *
 * This module holds only the fallback's VALUE. It has no imports on purpose: `dualWrite.ts` owns the
 * fetch and publishes into it (`setMirrorIntentBackendPolicy`), and the journal reads it, so neither
 * module has to load the other (and a suite that mocks one does not silently switch the other off).
 *
 * PARSING: `parseMirrorIntentAllowlist` (in `mirrorIntent.ts`) and `isMirrorIntentSbReadersOn` here
 * run the case table shared with the backend's refusal rule
 * (`apps/atlas-backend/src/common/utils/mirror-intent-policy-cases.json`).
 */

export const MIRROR_INTENT_ALLOWLIST_KEY = 'mirror_intent_org_allowlist'
export const MIRROR_INTENT_SB_READERS_KEY = 'mirror_intent_sb_readers'

export type MirrorIntentPolicySource = 'device-rc' | 'backend' | 'none'

export interface MirrorIntentBackendPolicy {
  allowlistRaw: string
  sbReadersRaw: string
  /** The template version the backend read, or null. */
  templateVersion: string | null
  /** DEVICE clock at receipt (as `dualWrite.ts`'s backend policy). */
  receivedAt: number
}

export interface MirrorIntentPolicy {
  allowlistRaw: string
  sbReadersRaw: string
  /**
   * Where the values came from: `device-rc` when this device's Remote Config read either key,
   * `backend` when it read neither and the backend's copy answered, `none` when nothing did.
   */
  source: MirrorIntentPolicySource
  /** Known only through the backend (the device SDKs do not expose it). */
  templateVersion: string | null
}

let backendPolicy: MirrorIntentBackendPolicy | null = null

/** Published by `dualWrite.ts` whenever it receives the backend policy. */
export function setMirrorIntentBackendPolicy(policy: MirrorIntentBackendPolicy): void {
  backendPolicy = policy
}

/** TEST-ONLY. */
export function resetMirrorIntentBackendPolicyForTests(): void {
  backendPolicy = null
}

/** `mirror_intent_sb_readers`: exactly `on` (trimmed, any case) is on; anything else is off. */
export function isMirrorIntentSbReadersOn(raw: unknown): boolean {
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'on'
}

/**
 * Read both keys. `readRemoteConfig` is the device SDK read (synchronous, may return `''`); a throw
 * reads as empty. Never throws.
 */
export function readMirrorIntentPolicy(readRemoteConfig: (key: string) => unknown): MirrorIntentPolicy {
  const device = (key: string): string => {
    try {
      const value = readRemoteConfig(key)
      return typeof value === 'string' ? value : ''
    } catch {
      return ''
    }
  }
  const deviceAllowlist = device(MIRROR_INTENT_ALLOWLIST_KEY)
  const deviceSbReaders = device(MIRROR_INTENT_SB_READERS_KEY)
  const fallback = backendPolicy
  const deviceRead = deviceAllowlist.trim() !== '' || deviceSbReaders.trim() !== ''
  return {
    allowlistRaw: deviceAllowlist.trim() ? deviceAllowlist : (fallback?.allowlistRaw ?? ''),
    sbReadersRaw: deviceSbReaders.trim() ? deviceSbReaders : (fallback?.sbReadersRaw ?? ''),
    source: deviceRead ? 'device-rc' : fallback ? 'backend' : 'none',
    templateVersion: fallback?.templateVersion ?? null,
  }
}
